/**
 * SipaReportingPage.jsx
 *
 * SIPA Reporting Comparison Page
 *
 * On load, displays all properties that have a SIPA for the previous month,
 * showing percentage change compared to the month before. Each property card
 * is clickable to open the detailed comparison view.
 *
 * Features:
 * - Monthly overview showing all properties with score changes
 * - Clickable property cards to drill into full comparison
 * - "Show changes only" filter (default checked) in comparison view
 * - Property selection dropdown for manual comparison
 * - Report pair selection (older + newer)
 * - Auto-suggest most recent two reports
 * - Full report rendering with change indicators
 * - Color-coded score deltas (green = improved, red = declined)
 * - Expandable change reason explanations
 * - Category and section-level score summaries
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import '@/styles/SipaReportingStyles.scss';
import {
    FaArrowUp,
    FaArrowDown,
    FaMinus,
    FaExchangeAlt,
    FaChevronDown,
    FaChevronRight,
    FaInfoCircle,
    FaExclamationCircle,
    FaClipboardList
} from 'react-icons/fa';
import * as Sentry from '@sentry/react';

const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const formatPeriod = (year, month) => {
    try {
        return `${monthNames[month - 1]} ${year}`;
    } catch (catchError) {
        Sentry.captureException(catchError);
        return `${year}-${String(month).padStart(2, '0')}`;
    }
};

const formatScore = (score) => {
    if (score === null || score === undefined) return '—';
    return `${Number(score).toFixed(2)}%`;
};

const ChangeIndicator = ({ direction, delta }) => {
    if (direction === 'no_comparison') return <span className="sipa-change-badge neutral"><FaMinus size={10} /> N/A</span>;
    if (direction === 'unchanged') return <span className="sipa-change-badge neutral"><FaMinus size={10} /> No change</span>;
    if (direction === 'improved') return <span className="sipa-change-badge improved"><FaArrowUp size={10} /> +{Number(delta).toFixed(2)}</span>;
    if (direction === 'declined') return <span className="sipa-change-badge declined"><FaArrowDown size={10} /> {Number(delta).toFixed(2)}</span>;
    return null;
};

const ControlRow = ({ control }) => {
    const [showReason, setShowReason] = useState(false);
    const hasChange = control.changeDirection !== 'unchanged' && control.changeDirection !== 'no_comparison';
    const rowClass = `sipa-compare-control-row ${hasChange ? `change-${control.changeDirection}` : ''}`;

    const resolveDisplayValue = (value) => {
        if (value === null || value === undefined) return '(not answered)';
        if (value === 'NA') return 'N/A';
        if (control.type === 'Checkbox') {
            // Normalize checkbox values to ensure legacy data (e.g. "True"/"FALSE"/boolean true)
            // is rendered consistently with other SIPA pages that use toLowerCase().
            const normalizedCheckboxValue = String(value).toLowerCase();
            return normalizedCheckboxValue === 'true' ? 'Yes' : 'No';
        }
        // Try to find option text
        if (control.options && control.options.length > 0) {
            const opt = control.options.find(o => o.value === value);
            if (opt) return opt.text;
        }
        return value;
    };

    return (
        <div className={rowClass}>
            <div className="control-header">
                <span className="control-label">{control.label}</span>
                {!control.excludeFromScore && (
                    <ChangeIndicator direction={control.changeDirection} delta={control.scoreDelta} />
                )}
                {control.excludeFromScore && (
                    <span className="sipa-change-badge info"><FaInfoCircle size={10} /> Not scored</span>
                )}
            </div>
            <div className="control-values">
                <div className="value-column older">
                    <span className="value-label">Previous:</span>
                    <span className="value-text">{resolveDisplayValue(control.olderValue)}</span>
                    {control.olderScore !== null && control.olderScore !== undefined && !control.excludeFromScore && (
                        <span className="value-score">({Number(control.olderScore).toFixed(1)} pts)</span>
                    )}
                </div>
                <div className="value-column newer">
                    <span className="value-label">Current:</span>
                    <span className="value-text">{resolveDisplayValue(control.newerValue)}</span>
                    {control.newerScore !== null && control.newerScore !== undefined && !control.excludeFromScore && (
                        <span className="value-score">({Number(control.newerScore).toFixed(1)} pts)</span>
                    )}
                </div>
            </div>
            {control.changeReason && (control.changeDirection === 'improved' || control.changeDirection === 'declined') && (
                <button className="reason-toggle" onClick={() => setShowReason(!showReason)}>
                    {showReason ? <FaChevronDown size={10} /> : <FaChevronRight size={10} />}
                    <span>Why did this change?</span>
                </button>
            )}
            {showReason && (
                <div className="change-reason">{control.changeReason}</div>
            )}
            {control.actionItemId && (
                <a
                    className="sipa-action-item-link"
                    href={`/action-items/${control.actionItemId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                >
                    <FaClipboardList size={12} />
                    <span>View Action Item</span>
                    {control.actionItemStatus && (
                        <span className={`action-item-status status-${(control.actionItemStatus || '').toLowerCase().replace(/\s+/g, '-')}`}>
                            {control.actionItemStatus}
                        </span>
                    )}
                </a>
            )}
        </div>
    );
};

const SubSectionBlock = ({ subSection, changesOnly }) => {
    const [collapsed, setCollapsed] = useState(false);
    const allControls = subSection.controls || [];
    const controls = changesOnly
        ? allControls.filter(c => c.changeDirection === 'improved' || c.changeDirection === 'declined')
        : allControls;
    const changedControls = allControls.filter(c => c.changeDirection === 'improved' || c.changeDirection === 'declined');

    if (changesOnly && controls.length === 0) return null;

    return (
        <div className="sipa-compare-subsection">
            <button
                className="subsection-header"
                type="button"
                aria-expanded={!collapsed}
                onClick={() => setCollapsed(!collapsed)}
            >
                {collapsed ? <FaChevronRight size={12} /> : <FaChevronDown size={12} />}
                <span className="subsection-title">{subSection.title}</span>
                {changedControls.length > 0 && (
                    <span className="changes-count">{changedControls.length} change{changedControls.length > 1 ? 's' : ''}</span>
                )}
            </button>
            {!collapsed && (
                <div className="subsection-controls">
                    {controls.map(ctrl => (
                        <ControlRow key={ctrl.id} control={ctrl} />
                    ))}
                </div>
            )}
        </div>
    );
};

const SectionBlock = ({ section, changesOnly }) => {
    const [collapsed, setCollapsed] = useState(false);
    const subSections = section.subSections || [];

    // If changesOnly, check if any subsection has changes
    const hasAnyChanges = subSections.some(sub =>
        (sub.controls || []).some(c => c.changeDirection === 'improved' || c.changeDirection === 'declined')
    );
    if (changesOnly && !hasAnyChanges) return null;

    return (
        <div className="sipa-compare-section">
            <button
                className="section-header"
                type="button"
                aria-expanded={!collapsed}
                onClick={() => setCollapsed(!collapsed)}
            >
                {collapsed ? <FaChevronRight size={14} /> : <FaChevronDown size={14} />}
                <h3 className="section-title">{section.title}</h3>
                {section.weight > 0 && <span className="section-weight">Weight: {section.weight}%</span>}
            </button>
            {!collapsed && (
                <div className="section-body">
                    {subSections.map(sub => (
                        <SubSectionBlock key={sub.id} subSection={sub} changesOnly={changesOnly} />
                    ))}
                </div>
            )}
        </div>
    );
};

const CategoryBlock = ({ category, changesOnly }) => {
    const sections = category.sections || [];

    // Aggregate changes count
    let totalChanges = 0;
    sections.forEach(sec => {
        (sec.subSections || []).forEach(sub => {
            (sub.controls || []).forEach(ctrl => {
                if (ctrl.changeDirection === 'improved' || ctrl.changeDirection === 'declined') totalChanges++;
            });
        });
    });

    if (changesOnly && totalChanges === 0) return null;

    return (
        <div className="sipa-compare-category">
            <div className="category-header">
                <h2>{category.category}</h2>
                {totalChanges > 0 && (
                    <span className="category-changes">{totalChanges} change{totalChanges > 1 ? 's' : ''}</span>
                )}
            </div>
            {sections.map(sec => (
                <SectionBlock key={sec.id} section={sec} changesOnly={changesOnly} />
            ))}
        </div>
    );
};

/**
 * Report tab definitions for extensibility.
 * Add new report types here to have them appear in the tab bar.
 */
const REPORT_TABS = [
    { id: 'comparison', label: 'Report Comparison', icon: <FaExchangeAlt /> },
    { id: 'overview', label: 'Monthly Overview', icon: <FaArrowUp /> },
    { id: 'problem-areas', label: 'Problem Areas', icon: <FaExclamationCircle /> }
];

const SipaReportingPage = () => {
    const { apiFetch } = useAuth();
    const [searchParams] = useSearchParams();
    const initializedFromUrl = useRef(false);

    // Active tab — defaults to overview for at-a-glance property dashboard on load
    const [activeTab, setActiveTab] = useState(() => {
        // If URL has comparison params, start on comparison tab
        if (searchParams.get('older') && searchParams.get('newer')) return 'comparison';
        return 'overview';
    });

    // State - Overview
    const [overview, setOverview] = useState(null);
    const [overviewLoading, setOverviewLoading] = useState(false);
    const [overviewError, setOverviewError] = useState(null);

    // State - Comparison detail
    const [properties, setProperties] = useState([]);
    const [propertyId, setPropertyId] = useState('');
    const [reports, setReports] = useState([]);
    const [olderReportId, setOlderReportId] = useState(() => searchParams.get('older') || '');
    const [newerReportId, setNewerReportId] = useState(() => searchParams.get('newer') || '');
    const [comparison, setComparison] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingReports, setLoadingReports] = useState(false);
    const [error, setError] = useState(null);
    const [changesOnly, setChangesOnly] = useState(true);

    // State - Problem Areas
    const [problemAreas, setProblemAreas] = useState(null);
    const [problemAreasLoading, setProblemAreasLoading] = useState(false);
    const [problemAreasError, setProblemAreasError] = useState(null);
    const [problemThreshold, setProblemThreshold] = useState(60);
    const [problemPeriodYear, setProblemPeriodYear] = useState(new Date().getFullYear());
    const [problemPeriodMonth, setProblemPeriodMonth] = useState(new Date().getMonth() + 1);

    // Load properties on mount (shared across tabs)
    useEffect(() => {
        const loadProperties = async () => {
            try {
                const res = await apiFetch('/api/Property/all');
                const data = await res.json();
                if (data.success && data.data) {
                    const active = data.data.filter(p => p.active || p.Active);
                    setProperties(active);
                }
            } catch (err) {
                Sentry.captureException(err);
                console.warn('Error loading properties:', err);
            }
        };
        loadProperties();
    }, [apiFetch]);

    // Auto-fetch comparison if URL params provided (e.g., linked from SIPA Reports table)
    useEffect(() => {
        if (initializedFromUrl.current) return;
        const older = searchParams.get('older');
        const newer = searchParams.get('newer');
        if (!older || !newer) return;
        initializedFromUrl.current = true;
        const autoCompare = async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await apiFetch(`/api/sipa/reports/compare?reportIdOlder=${older}&reportIdNewer=${newer}`);
                if (!res.ok) {
                    const text = await res.text();
                    setError(text || 'Failed to load comparison.');
                    return;
                }
                const data = await res.json();
                setComparison(data);
                setChangesOnly(true);
            } catch (err) {
                Sentry.captureException(err);
                setError('An error occurred while loading the comparison.');
            } finally {
                setLoading(false);
            }
        };
        autoCompare();
    }, [searchParams, apiFetch]);

    // Load monthly overview when that tab is active
    useEffect(() => {
        if (activeTab !== 'overview') return;
        if (overview) return; // Already loaded
        const loadOverview = async () => {
            setOverviewLoading(true);
            setOverviewError(null);
            try {
                const res = await apiFetch('/api/sipa/reports/monthly-overview');
                const data = await res.json();
                if (data.success) {
                    setOverview(data);
                } else {
                    setOverviewError(data.message || 'Failed to load overview.');
                }
            } catch (err) {
                Sentry.captureException(err);
                setOverviewError('An error occurred while loading the monthly overview.');
            } finally {
                setOverviewLoading(false);
            }
        };
        loadOverview();
    }, [activeTab, apiFetch, overview]);

    // Load reports when property changes (comparison tab)
    useEffect(() => {
        (async () => {
        if (!propertyId) {
            setReports([]);
            setOlderReportId('');
            setNewerReportId('');
            setComparison(null);
            return;
        }
        const loadReports = async () => {
            setLoadingReports(true);
            setComparison(null);
            setError(null);
            try {
                const res = await apiFetch(`/api/sipa/reports/list?propertyId=${propertyId}`);
                const data = await res.json();
                const list = Array.isArray(data) ? data : [];
                setReports(list);

                // Auto-select the two most recent reports
                if (list.length >= 2) {
                    setNewerReportId(String(list[0].id));
                    setOlderReportId(String(list[1].id));
                } else {
                    setNewerReportId(list.length > 0 ? String(list[0].id) : '');
                    setOlderReportId('');
                }
            } catch (err) {
                Sentry.captureException(err);
                console.error('Error loading reports:', err);
                setError('Failed to load reports for this property.');
            } finally {
                        setLoadingReports(false);
                        }
                    };
                    await loadReports();
                    })();
                }, [propertyId, apiFetch]);

    // Fetch comparison data
    const fetchComparison = useCallback(async () => {
        if (!olderReportId || !newerReportId) return;
        if (olderReportId === newerReportId) {
            setError('Please select two different reports to compare.');
            return;
        }
        setLoading(true);
        setError(null);
        setComparison(null);
        try {
            const res = await apiFetch(`/api/sipa/reports/compare?reportIdOlder=${olderReportId}&reportIdNewer=${newerReportId}`);
            if (!res.ok) {
                const text = await res.text();
                setError(text || 'Failed to load comparison.');
                return;
            }
            const data = await res.json();
            setComparison(data);
        } catch (err) {
            Sentry.captureException(err);
            console.error('Error fetching comparison:', err);
            setError('An error occurred while loading the comparison.');
        } finally {
            setLoading(false);
        }
    }, [olderReportId, newerReportId, apiFetch]);

    // Fetch problem areas
    const fetchProblemAreas = useCallback(async () => {
        setProblemAreasLoading(true);
        setProblemAreasError(null);
        setProblemAreas(null);
        try {
            const res = await apiFetch(
                `/api/sipa/reports/problem-areas?year=${problemPeriodYear}&month=${problemPeriodMonth}&threshold=${problemThreshold}`
            );
            const data = await res.json();
            if (data.success) {
                setProblemAreas(data.data);
            } else {
                setProblemAreasError(data.message || 'Failed to load problem areas.');
            }
        } catch (err) {
            Sentry.captureException(err);
            setProblemAreasError('An error occurred while loading problem areas.');
        } finally {
            setProblemAreasLoading(false);
        }
    }, [apiFetch, problemPeriodYear, problemPeriodMonth, problemThreshold]);

    // Auto-fetch problem areas when tab is active
    useEffect(() => {
        if (activeTab === 'problem-areas') {
            (async () => { await fetchProblemAreas(); })();
        }
    }, [activeTab, fetchProblemAreas]);

    // Handle clicking a property card from the overview
    const handleOverviewCardClick = useCallback((item) => {
        if (!item.previousReportId) return;
        setOlderReportId(String(item.previousReportId));
        setNewerReportId(String(item.currentReportId));
        setPropertyId(String(item.propertyId));
        setActiveTab('comparison');
        setComparison(null);
        setChangesOnly(true);
        setTimeout(async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await apiFetch(`/api/sipa/reports/compare?reportIdOlder=${item.previousReportId}&reportIdNewer=${item.currentReportId}`);
                if (!res.ok) {
                    const text = await res.text();
                    setError(text || 'Failed to load comparison.');
                    return;
                }
                const data = await res.json();
                setComparison(data);
            } catch (err) {
                Sentry.captureException(err);
                setError('An error occurred while loading the comparison.');
            } finally {
                setLoading(false);
            }
        }, 0);
    }, [apiFetch]);

    // Summary stats from comparison
    const summaryStats = useMemo(() => {
        if (!comparison || !comparison.structure) return null;
        let improved = 0, declined = 0, unchanged = 0, noComparison = 0;
        (comparison.structure || []).forEach(cat => {
            (cat.sections || []).forEach(sec => {
                (sec.subSections || []).forEach(sub => {
                    (sub.controls || []).forEach(ctrl => {
                        if (ctrl.changeDirection === 'improved') improved++;
                        else if (ctrl.changeDirection === 'declined') declined++;
                        else if (ctrl.changeDirection === 'unchanged') unchanged++;
                        else noComparison++;
                    });
                });
            });
        });
        return { improved, declined, unchanged, noComparison, total: improved + declined + unchanged + noComparison };
    }, [comparison]);

    // Available reports for dropdowns
    const olderOptions = reports.filter(r => String(r.id) !== newerReportId);
    const newerOptions = reports.filter(r => String(r.id) !== olderReportId);

    // ─── Render ────────────────────────────────────────────────────────────────

    return (
        <div className="sipa-page sipa-reporting-page">
            <div className="sipa-header-row">
                <h1><FaExchangeAlt /> SIPA Reporting</h1>
            </div>

            {/* Tab Bar */}
            <div className="sipa-tab-bar">
                {REPORT_TABS.map(tab => (
                    <button
                        key={tab.id}
                        type="button"
                        className={`sipa-tab ${activeTab === tab.id ? 'active' : ''}`}
                        onClick={() => setActiveTab(tab.id)}
                    >
                        {tab.icon}
                        <span>{tab.label}</span>
                    </button>
                ))}
            </div>

            {/* Tab Content */}
            <div className="sipa-tab-content">
                {/* ─── Comparison Tab ─── */}
                {activeTab === 'comparison' && (
                    <div className="sipa-tab-panel">
                        <div className="sipa-filters">
                            <label>
                                Property:
                                <select value={propertyId} onChange={e => setPropertyId(e.target.value)}>
                                    <option value="">— Select Property —</option>
                                    {properties.map(p => {
                                        const id = p.propertyID || p.PropertyID;
                                        const name = p.propertyName || p.PropertyName;
                                        return <option key={id} value={id}>{name}</option>;
                                    })}
                                </select>
                            </label>

                            {propertyId && !loadingReports && reports.length >= 2 && (
                                <>
                                    <label>
                                        Previous Report:
                                        <select value={olderReportId} onChange={e => setOlderReportId(e.target.value)}>
                                            <option value="">— Select —</option>
                                            {olderOptions.map(r => (
                                                <option key={r.id} value={r.id}>
                                                    {formatPeriod(r.year ?? r.periodYear, r.month ?? r.periodMonth)}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                    <label>
                                        Current Report:
                                        <select value={newerReportId} onChange={e => setNewerReportId(e.target.value)}>
                                            <option value="">— Select —</option>
                                            {newerOptions.map(r => (
                                                <option key={r.id} value={r.id}>
                                                    {formatPeriod(r.year ?? r.periodYear, r.month ?? r.periodMonth)}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                    <button
                                        className="btn-create"
                                        onClick={fetchComparison}
                                        disabled={!olderReportId || !newerReportId || loading}
                                    >
                                        {loading ? 'Comparing...' : 'Compare Reports'}
                                    </button>
                                </>
                            )}

                            {propertyId && !loadingReports && reports.length < 2 && (
                                <span className="sipa-info-text">This property needs at least 2 submitted reports to compare.</span>
                            )}
                            {loadingReports && <span className="sipa-info-text">Loading reports...</span>}
                        </div>

                        {error && <div className="sipa-error-banner">{error}</div>}

                        {comparison && summaryStats && (
                            <div className="sipa-compare-summary">
                                <div className="summary-header">
                                    <h2>Comparison: {comparison.olderReport?.periodLabel} → {comparison.newerReport?.periodLabel}</h2>
                                    <span className="property-label">{comparison.propertyName}</span>
                                </div>

                                <div className="summary-scores">
                                    <div className="score-card">
                                        <span className="score-label">Previous Total</span>
                                        <span className="score-value">{formatScore(comparison.olderReport?.total)}</span>
                                    </div>
                                    <div className="score-card">
                                        <span className="score-label">Current Total</span>
                                        <span className="score-value">{formatScore(comparison.newerReport?.total)}</span>
                                    </div>
                                    {comparison.olderReport?.total != null && comparison.newerReport?.total != null && (
                                        <div className={`score-card delta ${comparison.newerReport.total - comparison.olderReport.total >= 0 ? 'positive' : 'negative'}`}>
                                            <span className="score-label">Change</span>
                                            <span className="score-value">
                                                {comparison.newerReport.total - comparison.olderReport.total >= 0 ? '+' : ''}
                                                {(comparison.newerReport.total - comparison.olderReport.total).toFixed(2)}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                <div className="summary-stats-bar">
                                    <span className="stat improved"><FaArrowUp size={12} /> {summaryStats.improved} improved</span>
                                    <span className="stat declined"><FaArrowDown size={12} /> {summaryStats.declined} declined</span>
                                    <span className="stat unchanged"><FaMinus size={12} /> {summaryStats.unchanged} unchanged</span>
                                </div>

                                <div className="sipa-changes-only-toggle">
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={changesOnly}
                                            onChange={e => setChangesOnly(e.target.checked)}
                                        />
                                        Show changes only
                                    </label>
                                </div>
                            </div>
                        )}

                        {comparison && changesOnly && summaryStats && summaryStats.improved === 0 && summaryStats.declined === 0 && (
                            <div className="sipa-info-text" style={{ marginTop: '1rem' }}>
                                No changes detected between these two reports. Uncheck &quot;Show changes only&quot; to see all items.
                            </div>
                        )}

                        {comparison && (comparison.structure || []).map((cat, idx) => (
                            <CategoryBlock key={idx} category={cat} changesOnly={changesOnly} />
                        ))}

                        {!propertyId && !comparison && (
                            <div className="sipa-empty-state">
                                <FaExchangeAlt size={40} />
                                <h3>Select a property to compare reports</h3>
                                <p>Choose a property from the dropdown above, then select two report periods to see a detailed comparison of all SIPA scores.</p>
                            </div>
                        )}
                    </div>
                )}

                {/* ─── Monthly Overview Tab ─── */}
                {activeTab === 'overview' && (
                    <div className="sipa-tab-panel">
                        {overviewLoading && <div className="sipa-info-text">Loading monthly overview...</div>}
                        {overviewError && <div className="sipa-error-banner">{overviewError}</div>}

                        {overview && (
                            <>
                                <div className="sipa-overview-header">
                                    <h2>
                                        {formatPeriod(overview.targetYear, overview.targetMonth)}
                                    </h2>
                                    <p className="sipa-overview-subtitle">
                                        Compared to {formatPeriod(overview.prevYear, overview.prevMonth)}. Click a property to view the full comparison.
                                    </p>
                                </div>

                                {overview.data.length === 0 && (
                                    <div className="sipa-info-text">No SIPA reports found for {formatPeriod(overview.targetYear, overview.targetMonth)}.</div>
                                )}

                                <div className="sipa-overview-grid">
                                    {overview.data.map(item => (
                                        <button
                                            key={item.propertyId}
                                            className={`sipa-overview-card ${item.direction}`}
                                            onClick={() => handleOverviewCardClick(item)}
                                            disabled={!item.previousReportId}
                                            type="button"
                                        >
                                            <div className="card-property-name">{item.propertyName}</div>
                                            <div className="card-score">
                                                {item.currentTotal != null ? `${Number(item.currentTotal).toFixed(1)}%` : '—'}
                                            </div>
                                            <div className={`card-change ${item.direction}`}>
                                                {item.direction === 'improved' && <><FaArrowUp size={12} /> +{Number(item.change).toFixed(1)}</>}
                                                {item.direction === 'declined' && <><FaArrowDown size={12} /> {Number(item.change).toFixed(1)}</>}
                                                {item.direction === 'unchanged' && <><FaMinus size={10} /> No change</>}
                                                {item.direction === 'no_comparison' && <span className="no-prev">No previous report</span>}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* ─── Problem Areas Tab ─── */}
                {activeTab === 'problem-areas' && (
                    <div className="sipa-tab-panel">
                        <div className="sipa-filters">
                            <label>
                                Period:
                                <select
                                    value={problemPeriodMonth}
                                    onChange={e => setProblemPeriodMonth(Number(e.target.value))}
                                >
                                    {monthNames.map((m, i) => (
                                        <option key={i + 1} value={i + 1}>{m}</option>
                                    ))}
                                </select>
                            </label>
                            <label>
                                Year:
                                <select
                                    value={problemPeriodYear}
                                    onChange={e => setProblemPeriodYear(Number(e.target.value))}
                                >
                                    {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(y => (
                                        <option key={y} value={y}>{y}</option>
                                    ))}
                                </select>
                            </label>
                            <label>
                                Threshold (%):
                                <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    value={problemThreshold}
                                    onChange={e => setProblemThreshold(Number(e.target.value))}
                                    className="threshold-input"
                                />
                            </label>
                            <button className="btn-create" onClick={fetchProblemAreas} disabled={problemAreasLoading}>
                                {problemAreasLoading ? 'Analysing...' : 'Analyse'}
                            </button>
                        </div>
                        <p className="sipa-overview-subtitle">
                            Highlights controls scoring below {problemThreshold}% across all properties for the selected period.
                        </p>

                        {problemAreasError && <div className="sipa-error-banner">{problemAreasError}</div>}

                        {problemAreas && problemAreas.length === 0 && (
                            <div className="sipa-info-text" style={{ marginTop: '1rem' }}>
                                No problem areas found — all controls are scoring at or above {problemThreshold}% for this period. 🎉
                            </div>
                        )}

                        {problemAreas && problemAreas.length > 0 && (
                            <div className="sipa-problem-areas-table-wrapper">
                                <table className="sipa-problem-areas-table">
                                    <thead>
                                        <tr>
                                            <th>Property</th>
                                            <th>Category</th>
                                            <th>Section</th>
                                            <th>Control</th>
                                            <th>Score</th>
                                            <th>Max Score</th>
                                            <th>%</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {problemAreas.map((item, idx) => (
                                            <tr key={idx} className={item.percentage === 0 ? 'row-critical' : 'row-warning'}>
                                                <td>{item.propertyName}</td>
                                                <td>{item.category}</td>
                                                <td>{item.section}</td>
                                                <td className="control-cell">{item.controlLabel}</td>
                                                <td className="score-cell">{Number(item.score).toFixed(1)}</td>
                                                <td className="score-cell">{Number(item.maxScore).toFixed(1)}</td>
                                                <td className="score-cell percentage-cell">
                                                    <span className={`percentage-badge ${item.percentage === 0 ? 'critical' : 'warning'}`}>
                                                        {Number(item.percentage).toFixed(0)}%
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {!problemAreas && !problemAreasLoading && !problemAreasError && (
                            <div className="sipa-empty-state">
                                <FaExclamationCircle size={40} />
                                <h3>Problem Area Analysis</h3>
                                <p>Select a period and threshold above, then click &quot;Analyse&quot; to identify controls that are scoring poorly across your portfolio.</p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SipaReportingPage;
